import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { FileText, ImageIcon, ZoomIn } from "lucide-react";

// Modul-Level-Cache, damit Thumbnails nicht bei jedem Re-Render neu geladen werden.
const blobUrlCache = new Map();

async function fetchReceiptBlobUrl(receiptId) {
  if (blobUrlCache.has(receiptId)) return blobUrlCache.get(receiptId);
  try {
    const r = await apiClient.get(`/receipts/${receiptId}/file`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(r.data);
    blobUrlCache.set(receiptId, url);
    return url;
  } catch {
    return null;
  }
}

export function ReceiptThumbnail({ receipt, onZoom, size = 48 }) {
  const [url, setUrl] = useState(() =>
    blobUrlCache.get(receipt?._id) || null
  );
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Iter169-Fix: Fallback über Dateiname-Extension, wenn mime_type fehlt
  // (z. B. bei Belegen, die aus dem Posteingang übernommen wurden).
  const rawMime = (receipt?.mime_type || receipt?.content_type || "").toLowerCase();
  const fnameLower = (receipt?.file_name || receipt?.original_filename || receipt?.file_path || "").toLowerCase();
  const extImage = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(fnameLower);
  const extPdf = /\.pdf$/i.test(fnameLower);
  const isImage = rawMime.startsWith("image/") || (!rawMime && extImage);
  const isPdf = rawMime === "application/pdf" || (!rawMime && extPdf);
  const hasFile = Boolean(
    receipt?.file_path || receipt?.file_id || receipt?.file_name || receipt?.original_filename,
  );

  useEffect(() => {
    let cancelled = false;
    if (!receipt?._id || !isImage || url) return;
    setLoading(true);
    fetchReceiptBlobUrl(receipt._id).then((u) => {
      if (cancelled) return;
      if (u) setUrl(u);
      else setFailed(true);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [receipt?._id, isImage, url]);

  const containerStyle = { width: size, height: size };

  if (!hasFile) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50 text-slate-300"
        style={containerStyle}
        data-testid={`receipt-thumb-empty-${receipt?._id}`}
        title="Kein Bild verknüpft"
      >
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }

  if (isPdf) {
    return (
      <button
        type="button"
        onClick={() => onZoom?.(receipt)}
        className="flex items-center justify-center rounded-md border border-slate-200 bg-rose-50 text-rose-600 hover:bg-rose-100 transition"
        style={containerStyle}
        data-testid={`receipt-thumb-pdf-${receipt?._id}`}
        title="PDF öffnen"
      >
        <FileText className="h-5 w-5" />
      </button>
    );
  }

  if (failed) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-300"
        style={containerStyle}
        data-testid={`receipt-thumb-failed-${receipt?._id}`}
        title="Bild nicht ladbar"
      >
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }

  if (loading || !url) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-slate-200 bg-slate-100 animate-pulse"
        style={containerStyle}
        data-testid={`receipt-thumb-loading-${receipt?._id}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => onZoom?.(receipt)}
      className="group relative rounded-md border border-slate-200 overflow-hidden hover:ring-2 hover:ring-violet-400 transition"
      style={containerStyle}
      data-testid={`receipt-thumb-img-${receipt?._id}`}
      title="Beleg vergrössern"
    >
      <img
        src={url}
        alt="Beleg"
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition">
        <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100" />
      </div>
    </button>
  );
}

export default ReceiptThumbnail;
